/**
 * Helpers that turn Keystone API responses into table rows and display values.
 * They only inspect the response shape, so they work for every policy and
 * runtime endpoint without per-endpoint code.
 */

export type Json = null | boolean | number | string | Json[] | { [key: string]: Json };
export type JsonObject = { [key: string]: Json };

/** A display value for one table cell. */
export type CellValue =
  | { kind: 'empty' }
  | { kind: 'text'; text: string }
  | { kind: 'principal'; type: string; value: string }
  | { kind: 'badges'; items: string[] }
  | { kind: 'paths'; items: string[] };

export const PATH_SEPARATOR = ' › ';

export function isObject(v: unknown): v is JsonObject {
  return typeof v === 'object' && v !== null && !Array.isArray(v);
}

function isPrincipal(v: JsonObject): boolean {
  return typeof v.type === 'string' && 'value' in v;
}

function isNamed(v: unknown): v is JsonObject & { name: string } {
  return isObject(v) && typeof v.name === 'string';
}

/** Child resources can come back under either key. */
function childResources(v: JsonObject): Json[] | undefined {
  const children = v.resources ?? v.children;
  return Array.isArray(children) && children.length > 0 ? children : undefined;
}

function isResource(v: JsonObject): boolean {
  return typeof v.name === 'string' && (Array.isArray(v.scopes) || childResources(v) !== undefined);
}

function resourceLabel(v: JsonObject): string {
  const scopes = Array.isArray(v.scopes) && v.scopes.length > 0 ? ` [${v.scopes.join(', ')}]` : '';
  return `${v.name as string}${scopes}`;
}

/** Flattens a nested resource into one path per leaf, e.g. "yeti › bom › tier". */
export function resourcePaths(v: JsonObject, prefix = ''): string[] {
  const here = prefix ? `${prefix}${PATH_SEPARATOR}${resourceLabel(v)}` : resourceLabel(v);
  const named = childResources(v)?.filter(isNamed) ?? [];
  if (named.length === 0) return [here];
  return named.flatMap(c => resourcePaths(c, here));
}

function compactText(v: Json): string {
  if (v === null) return '-';
  if (typeof v !== 'object') return String(v);
  if (Array.isArray(v)) return v.map(compactText).join(', ');
  return Object.entries(v)
    .map(([k, val]) => `${k}: ${compactText(val)}`)
    .join(', ');
}

export function toCellValue(v: Json | undefined): CellValue {
  if (v === undefined || v === null || v === '') return { kind: 'empty' };
  if (typeof v !== 'object') return { kind: 'text', text: String(v) };

  if (Array.isArray(v)) {
    if (v.length === 0) return { kind: 'empty' };
    if (v.every(isObject) && v.some(isResource)) {
      return { kind: 'paths', items: v.flatMap(r => (isNamed(r) ? resourcePaths(r) : [compactText(r)])) };
    }
    if (v.every(isObject) && v.every(isPrincipal)) {
      return { kind: 'badges', items: v.map(p => `${p.type as string}: ${compactText(p.value)}`) };
    }
    return { kind: 'badges', items: v.map(item => (isNamed(item) ? item.name : compactText(item))) };
  }

  if (isPrincipal(v)) return { kind: 'principal', type: v.type as string, value: compactText(v.value) };
  if (isResource(v)) return { kind: 'paths', items: resourcePaths(v) };
  if (isNamed(v) && Object.keys(v).length === 1) return { kind: 'text', text: v.name };
  return { kind: 'text', text: compactText(v) };
}

/** Plain text for a cell, used for sorting and filtering. */
export function cellText(v: Json | undefined): string {
  const cell = toCellValue(v);
  switch (cell.kind) {
    case 'empty':
      return '';
    case 'text':
      return cell.text;
    case 'principal':
      return `${cell.type} ${cell.value}`;
    case 'badges':
    case 'paths':
      return cell.items.join(' ');
  }
}

/** "user_group" -> "User group". */
export function columnLabel(key: string): string {
  const words = key.replace(/[_-]+/g, ' ').trim();
  return words.charAt(0).toUpperCase() + words.slice(1);
}

export interface TableModel {
  columns: string[];
  rows: JsonObject[];
}

/**
 * Builds table columns and rows from a response.
 * Returns null when the response is an empty object or list (e.g. a successful DELETE, or no matches).
 */
export function toTableModel(data: unknown): TableModel | null {
  const json = data as Json;
  if (Array.isArray(json)) {
    if (json.length === 0) return null;
    if (json.every(isObject)) {
      const columns: string[] = [];
      for (const row of json) {
        for (const key of Object.keys(row)) if (!columns.includes(key)) columns.push(key);
      }
      return { columns, rows: json };
    }
    return { columns: ['value'], rows: json.map(value => ({ value })) };
  }
  if (isObject(json)) {
    if (Object.keys(json).length === 0) return null;
    return { columns: Object.keys(json), rows: [json] };
  }
  return { columns: ['value'], rows: [{ value: json }] };
}
